import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { EMAIL_RE, ErrorSummary, Field } from "../components/form";
import { useI18n } from "../i18n/I18nProvider";
import { api, ApiError } from "../lib/api";

type Fields = "orderNumber" | "name" | "email" | "phone" | "reason";
const IDS: Record<string, string> = { orderNumber: "wd-order", name: "wd-name", email: "wd-email" };

/**
 * "Botón de arrepentimiento" (Res. SCI 424/2020): revoke a purchase without an account and get a
 * request code on the spot. Linked from the top of the home page and from the footer.
 */
export function WithdrawalPage() {
  const { t, locale } = useI18n();
  const auth = useAuth();
  const [form, setForm] = useState<Record<Fields, string>>({ orderNumber: "", name: auth.user?.name ?? "", email: auth.user?.email ?? "", phone: "", reason: "" });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<{ code: string; orderFound: boolean } | null>(null);
  const set = (k: Fields) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const found: Record<string, string> = {};
    if (!/^[A-Za-z0-9-]{3,40}$/.test(form.orderNumber.trim())) found.orderNumber = "withdraw.errors.order";
    if (form.name.trim().length < 2) found.name = "auth.errors.name";
    if (!EMAIL_RE.test(form.email.trim())) found.email = "auth.errors.email";
    setErrors(found);
    setAttempt((n) => n + 1);
    if (Object.keys(found).length) return;
    setBusy(true);
    try {
      setDone(await api.requestWithdrawal({ ...form, locale }));
    } catch (err) {
      setErrors({ form: err instanceof ApiError && err.status === 429 ? "auth.errors.tooMany" : "auth.errors.generic" });
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section className="container section narrow">
        <h1>{t("withdraw.doneTitle")}</h1>
        <p>{t("withdraw.doneCode")}</p>
        <p className="withdraw-code" aria-live="polite">
          {done.code}
        </p>
        <p>{t("withdraw.doneText")}</p>
        {!done.orderFound && <p className="muted">{t("withdraw.notFound")}</p>}
        <Link to="/" className="btn btn-primary">
          {t("notFound.back")}
        </Link>
      </section>
    );
  }

  return (
    <section className="container section narrow">
      <h1>{t("withdraw.title")}</h1>
      <p>{t("withdraw.intro")}</p>
      <p className="muted">
        {t("withdraw.more")} <Link to="/reembolsos">{t("footer.refunds")}</Link>.
      </p>
      <ErrorSummary errors={errors} fieldIds={IDS} focusKey={attempt} />
      <form onSubmit={submit} noValidate>
        <Field id="wd-order" label={t("withdraw.order")} hint={t("withdraw.orderHint")} value={form.orderNumber} onChange={set("orderNumber")} autoComplete="off" required error={errors.orderNumber ? t("withdraw.errors.order") : null} />
        <Field id="wd-name" label={t("withdraw.name")} value={form.name} onChange={set("name")} autoComplete="name" required error={errors.name ? t("auth.errors.name") : null} />
        <Field id="wd-email" type="email" label={t("withdraw.email")} value={form.email} onChange={set("email")} autoComplete="email" required error={errors.email ? t("auth.errors.email") : null} />
        <Field id="wd-phone" type="tel" inputMode="tel" label={t("withdraw.phone")} value={form.phone} onChange={set("phone")} autoComplete="tel" />
        <div className="field">
          <label htmlFor="wd-reason">{t("withdraw.reason")}</label>
          <textarea id="wd-reason" rows={3} maxLength={1000} value={form.reason} onChange={set("reason")} />
        </div>
        <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
          {busy ? t("withdraw.sending") : t("withdraw.submit")}
        </button>
      </form>
    </section>
  );
}
